import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate, useLocation } from "react-router-dom";
import "./subNiche.css";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

export default function SubNicheSelect() {
  const navigate = useNavigate();
  const location = useLocation();
  const isFetched = useRef(false);

  const selectedNiche =
    location.state?.niche ||
    JSON.parse(localStorage.getItem("selectedNiche") || "null") ||
    {};

  const [subNiches, setSubNiches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [userTimeZone, setUserTimeZone] = useState("UTC");

  useEffect(() => {
    const timeZone = getUserTimeZone();
    const userStatus = localStorage.getItem("user_status") || "individual";

    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);

    sessionStorage.setItem("onboarding_force_route", "/subniche-select");
    sessionStorage.setItem("onboarding_force_status", userStatus);
  }, []);

  useEffect(() => {
    const userStatus = localStorage.getItem("user_status");
    const subNicheCode = userStatus === "salon" ? "226" : "214";
    localStorage.setItem("onboarding_code", subNicheCode);
  }, []);

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    if (!token) return null;

    const verifyHeaders = buildHeaders(null);

    const verifyRes = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers: verifyHeaders,
      body: JSON.stringify({ token }),
    });

    if (verifyRes.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!refresh) return null;

    const refreshRes = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers: verifyHeaders,
      body: JSON.stringify({ refresh }),
    });

    const data = await refreshRes.json().catch(() => ({}));

    if (refreshRes.ok && data.access) {
      const useLocal =
        !!localStorage.getItem("accessToken") ||
        !!localStorage.getItem("refreshToken");

      if (useLocal) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  useEffect(() => {
    if (!selectedNiche?.id) {
      setError("No niche selected. Please choose a niche first.");
      setPageLoading(false);
      return;
    }

    if (isFetched.current) return;
    isFetched.current = true;

    const fetchSubNiches = async () => {
      try {
        setPageLoading(true);
        setError("");

        const token = await getValidToken();

        if (!token) {
          setError("Session expired. Please log in again.");
          setPageLoading(false);
          return;
        }

        const headers = buildHeaders(token);

        const res = await fetch(`${BASE_URL}/auth/profession-categories/`, {
          method: "GET",
          headers,
        });

        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(`Failed (${res.status})`);
        }

        const found = Array.isArray(data)
          ? data.find(
              (item) =>
                Number(item.id) === Number(selectedNiche.id) ||
                item.title === selectedNiche.name ||
                item.title === selectedNiche.title
            )
          : null;

        const mapped =
          found?.profesions_list?.length
            ? found.profesions_list.map((p) => ({
                id: Number(p.id),
                label: p.title || p.name || "Unnamed",
              }))
            : [];

        setSubNiches(mapped);
      } catch (err) {
        console.error("Failed to load subcategories:", err);
        setError("Failed to load subcategories.");
      } finally {
        setPageLoading(false);
      }
    };

    fetchSubNiches();
  }, [selectedNiche?.id, selectedNiche?.name, selectedNiche?.title]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const alreadySelected = prev.includes(id);

      if (alreadySelected) {
        return prev.filter((x) => x !== id);
      }

      if (prev.length >= 3) {
        return prev;
      }

      return [...prev, id];
    });
  };

  const handleBack = () => navigate(-1);

  const handleNext = async () => {
    const token = await getValidToken();

    if (!token) {
      setError("Authorization required. Please log in again.");
      return;
    }

    if (!selected.length) {
      setError("Please select at least one specialty.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const headers = buildHeaders(token);

      const saveProfessionsRes = await fetch(`${BASE_URL}/auth/set-professions/`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          professions: selected.map(Number),
          timeZone: userTimeZone,
        }),
      });

      let saveProfessionsData = {};
      const contentType = saveProfessionsRes.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        saveProfessionsData = await saveProfessionsRes.json().catch(() => ({}));
      }

      if (!saveProfessionsRes.ok) {
        throw new Error(
          saveProfessionsData?.detail || `Failed (${saveProfessionsRes.status})`
        );
      }

      localStorage.setItem("selectedSubNiches", JSON.stringify(selected));
      localStorage.setItem("subniche_selection_time", new Date().toISOString());
      localStorage.setItem("subniche_timezone", userTimeZone);

      const userStatus = localStorage.getItem("user_status") || "individual";
      const nextCode = userStatus === "salon" ? "228" : "216";
      localStorage.setItem("onboarding_code", nextCode);

      sessionStorage.setItem("onboarding_force_route", "/suggested-services");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      navigate("/suggested-services", {
        replace: true,
        state: {
          niche: selectedNiche,
          selectedSubNiches: selected,
          timeZone: userTimeZone,
        },
      });
    } catch (err) {
      console.error("Error saving sub-niches:", err);
      setError(err.message || "Network error. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && selected.length > 0 && !loading) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [selected, loading]);

  return (
    <motion.div
      className="screen-bg"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <main className="container-card">
        <div className="header-row space">
          <div className="title-wrap">
            <h2>Choose Your Specialty</h2>
            <p>Select the specific services you offer</p>
          </div>
        </div>

        <div className="two-col">
          <div className="cover-card">
            <img
              src={
                selectedNiche?.icon ||
                selectedNiche?.image_url ||
                "https://images.unsplash.com/photo-1606156867278-48b7f1fdfa4d?q=80&w=1600&auto=format&fit=crop"
              }
              alt={selectedNiche?.title || selectedNiche?.name || "Selected niche"}
            />
            <div className="cover-title">
              {selectedNiche?.title || selectedNiche?.name || "Selected niche"}
            </div>
          </div>

          <div className="select-col">
            <h4 className="up-to">Select up to 3</h4>
            {error && <p className="error-text">{error}</p>}

            <div className="pill-list">
              {pageLoading ? (
                <p className="loading-text">Loading specialties...</p>
              ) : subNiches.length > 0 ? (
                subNiches.map((item) => {
                  const active = selected.includes(item.id);
                  const disableOthers =
                    selected.length >= 3 && !selected.includes(item.id);

                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`pill ${active ? "checked" : ""}`}
                      onClick={() => toggleSelect(item.id)}
                      disabled={disableOthers || loading}
                    >
                      <span>{item.label}</span>
                      <span className="checkbox">
                        <span className="mark" />
                      </span>
                    </button>
                  );
                })
              ) : (
                <p className="loading-text">No specialties found.</p>
              )}
            </div>
          </div>
        </div>

        <div className="bottom-row">
          <motion.button
            className="primary-btn"
            disabled={!selected.length || loading || pageLoading}
            onClick={handleNext}
            whileHover={{ scale: selected.length ? 1.04 : 1 }}
            whileTap={{ scale: selected.length ? 0.96 : 1 }}
          >
            {loading ? "Saving..." : "Next"}
          </motion.button>

          <span className="step">Step 3/5</span>
        </div>
      </main>
    </motion.div>
  );
}